import { Button, Tag, Tooltip } from '@cimpress/react-components';
import ListView from '../components/ListView';
import { rest } from 'lodash';
import { useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useFulfiller } from '../FulfillerContext';
import { EntityType, FetchEntityTypesQueryParams } from '../models/EntityType';
import { fetchEntityTypes } from '../services/entity.management.service';
import { COLUMN_DEFINITIONS, COLUMN_HEADERS } from '../constants';
import {
  toTitleCase,
  formatRelativeTime,
  handleSort,
} from '../utilities/common';
import '../css/common.css';
import ListViewZeroState from '../components/ListViewZeroState';
import styles from '../css/pages.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';

const columns = [
  {
    Header: COLUMN_HEADERS.NAME,
    accessor: COLUMN_DEFINITIONS.NAME,
    headerStyle: {
      textTransform: 'none',
    },
    filterable: false,
    sortable: false,
  },
  {
    Header: COLUMN_HEADERS.VERSION_ID,
    accessor: COLUMN_DEFINITIONS.VERSION_ID,
    headerStyle: {
      textTransform: 'none',
    },
    filterable: false,
    sortable: false,
  },
  {
    Header: COLUMN_HEADERS.LATEST_UPDATE,
    accessor: COLUMN_DEFINITIONS.LATEST_UPDATE,
    headerStyle: {
      textTransform: 'none',
    },
    filterable: false,
    sortable: true,
  },
  {
    Header: COLUMN_HEADERS.DESCRIPTION,
    accessor: COLUMN_DEFINITIONS.DESCRIPTION,
    headerStyle: {
      textTransform: 'none',
    },
    filterable: false,
    sortable: false,
  },
  {
    Header: COLUMN_HEADERS.TAGS,
    accessor: COLUMN_DEFINITIONS.TAGS,
    headerStyle: {
      textTransform: 'none',
    },
    filterable: false,
    sortable: false,
  },
];

const updateTableData = (
  entityTypes: EntityType[],
  navigateToDetails: (id: string) => void,
) => {
  return entityTypes.map((et) => ({
    ...et,
    [COLUMN_DEFINITIONS.NAME]: (
      <button
        className="button-link"
        onClick={() => navigateToDetails(et.entityTypeId)}
      >
        {toTitleCase(et.entityTypeName)}
      </button>
    ),
    [COLUMN_DEFINITIONS.LATEST_UPDATE]: formatRelativeTime(
      et.updatedTimestamp ?? '',
    ),
    [COLUMN_DEFINITIONS.TAGS]: et.tags?.map((tag) => (
      <Tag key={tag} value={tag} className={styles.tableTag} />
    )),
    [COLUMN_DEFINITIONS.VERSION_ID]: et.versionId,
  }));
};
const EntityManagementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenantId');
  const { fulfillerData } = useFulfiller();
  const canCreate = useSelector((state: RootState) => state.permission.create);
  const locationHook = useLocation();
  const navigateToDetails = useCallback(
    (id: string) => {
      navigate(`${locationHook.pathname}/${id}${locationHook.search}`);
    },
    [locationHook.pathname, locationHook.search, navigate],
  );

  const fetchData = useCallback(async () => {
    if (
      fulfillerData?.fulfillerLocation.value &&
      tenant === fulfillerData?.fulfillerLocation.value
    ) {
      return await fetchEntityTypes(
        fulfillerData.fulfillerLocation.value,
        {} as FetchEntityTypesQueryParams,
      );
    }
    return [];
  }, [fulfillerData, tenant]);

  const tableConfig = {
    columns,
    keyField: 'id',
    loadingText: 'Fetching Entity Types...',
    noDataText: 'No Data available!',
    paginationSummary: (count: number) => `Showing ${count} of ${count}`,
  };

  const CreateEntityTypeButton = canCreate ? (
    <Button
      variant="default"
      onClick={() =>
        navigate(
          `/entitytypes/create?tenantId=${fulfillerData?.fulfillerLocation.value}`,
        )
      }
    >
      + Create Entity Type
    </Button>
  ) : (
    <Tooltip
      contents="You do not have permission to create entity type"
      direction="left"
    >
      <Button variant="default" disabled={true}>
        + Create Entity Type
      </Button>
    </Tooltip>
  );

  const ListViewZeroStateElement = (
    <ListViewZeroState Button={CreateEntityTypeButton} />
  );

  return (
    <div
      className="layout-container"
      style={{ display: 'flex', flexDirection: 'column', margin: '16px' }}
      {...rest}
    >
      <ListView<EntityType>
        key={fulfillerData?.fulfillerLocation.value}
        fetchData={fetchData}
        updateTableData={updateTableData}
        navigateToDetails={navigateToDetails}
        tableConfig={tableConfig}
        handleSort={handleSort<EntityType>}
        ListViewZeroState={ListViewZeroStateElement}
        CreateButton={CreateEntityTypeButton}
      />
    </div>
  );
};

export default EntityManagementPage;
